import React, { useState, useRef } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
  LinearProgress,
  Chip,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  TextField,
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  TableChart as ExcelIcon,
  CheckCircle as ValidIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  Inventory as ProductIcon,
  LocationOn as LocationIcon,
  Assessment as AnalysisIcon,
  Download as DownloadIcon,
  PlayArrow as StartIcon,
  Refresh as RefreshIcon,
  Storage as DatabaseIcon,
} from '@mui/icons-material';
import { formatCurrency } from '../utils/currency';

interface ExcelFile {
  name: string;
  size: number;
  lastModified: Date;
  status: 'pending' | 'analyzing' | 'ready' | 'migrated' | 'error';
  location?: string;
  records?: ExcelRecord[];
  analysis?: FileAnalysis;
}

interface ExcelRecord {
  row: number;
  name?: string;
  sku?: string;
  description?: string;
  price?: number;
  cost?: number;
  stock?: number;
  category?: string;
  unit?: string;
  supplier?: string;
  location?: string;
  status: 'valid' | 'invalid' | 'duplicate';
  errors: string[];
  warnings: string[];
}

interface FileAnalysis {
  totalRows: number;
  validRows: number;
  invalidRows: number;
  duplicates: number;
  categories: string[];
  priceRange: { min: number; max: number };
  stockTotal: number;
  estimatedValue: number;
}

interface MigrationSettings {
  overwriteExisting: boolean;
  generateMissingBarcodes: boolean;
  assignDefaultCategory: boolean;
  defaultCategory: string;
  priceMultiplier: number;
  stockValidation: boolean;
}

const MigrationPage: React.FC = () => {
  const [files, setFiles] = useState<ExcelFile[]>([]);
  const [currentStep, setCurrentStep] = useState(0);
  const [selectedFile, setSelectedFile] = useState<ExcelFile | null>(null);
  const [migrationSettings, setMigrationSettings] = useState<MigrationSettings>({
    overwriteExisting: false,
    generateMissingBarcodes: true,
    assignDefaultCategory: true,
    defaultCategory: 'Otros',
    priceMultiplier: 1.0,
    stockValidation: true,
  });
  const [migrationProgress, setMigrationProgress] = useState(0);
  const [migrationDialog, setMigrationDialog] = useState(false);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [isMigrating, setIsMigrating] = useState(false);
  
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Archivos reales con ~10,000 productos distribuidos
  const existingFiles: ExcelFile[] = [
    {
      name: 'ListaInvFisicAlmacenPrincipal.xls',
      size: 2456000, // ~2.5MB - archivo principal con más productos
      lastModified: new Date('2024-01-10'),
      status: 'pending',
      location: 'Almacén Principal',
    },
    {
      name: 'ListaInvFisicBodega3.xls',
      size: 1832000, // ~1.8MB - bodega secundaria
      lastModified: new Date('2024-01-08'),
      status: 'pending',
      location: 'Bodega 3',
    },
    {
      name: 'ListaInvFisicPlasticos2.xls',
      size: 1423000, // ~1.4MB - especializada en plásticos
      lastModified: new Date('2024-01-05'),
      status: 'pending',
      location: 'Plásticos 2',
    },
  ];

  React.useEffect(() => {
    setFiles(existingFiles);
  }, []);

  // Simular análisis de archivo Excel
  const analyzeExcelFile = async (file: ExcelFile): Promise<void> => {
    setIsAnalyzing(true);
    
    // Simular análisis de archivo grande (más tiempo para archivos reales)
    await new Promise(resolve => setTimeout(resolve, 5000));
    
    // Generar datos realistas basados en el archivo seleccionado
    let totalRows, validRows, invalidRows, duplicates, estimatedValue, stockTotal;
    
    if (file.name === 'ListaInvFisicAlmacenPrincipal.xls') {
      totalRows = 4821;
      validRows = 4523;
      invalidRows = 198;
      duplicates = 100;
      stockTotal = 125430;
      estimatedValue = 15650000; // ~15.6M Lempiras
    } else if (file.name === 'ListaInvFisicBodega3.xls') {
      totalRows = 3245;
      validRows = 3012;
      invalidRows = 145;
      duplicates = 88;
      stockTotal = 89320;
      estimatedValue = 9840000; // ~9.8M Lempiras
    } else { // Plásticos 2
      totalRows = 2634;
      validRows = 2456;
      invalidRows = 124;
      duplicates = 54;
      stockTotal = 67890;
      estimatedValue = 7320000; // ~7.3M Lempiras
    }

    // Muestra de registros para mostrar variedad
    const mockRecords: ExcelRecord[] = [
      {
        row: 2,
        name: 'Plato Plástico Hondo 8"',
        sku: 'PLA-002-' + file.location?.substring(0,3).toUpperCase(),
        description: 'Plato hondo de plástico resistente',
        price: 28.50,
        cost: 22.00,
        stock: Math.floor(Math.random() * 500) + 50,
        category: 'Hogar',
        unit: 'PZA',
        supplier: 'Proveedores Unidos HN',
        location: file.location,
        status: 'valid',
        errors: [],
        warnings: [],
      },
      {
        row: 3,
        name: 'Vaso Plástico Mediano 10oz',
        sku: 'VAS-002-' + file.location?.substring(0,3).toUpperCase(),
        description: 'Vaso de plástico transparente mediano',
        price: 18.75,
        cost: 14.50,
        stock: Math.floor(Math.random() * 800) + 100,
        category: 'Hogar',
        unit: 'PZA',
        supplier: 'Plásticos de Centro América',
        location: file.location,
        status: 'valid',
        errors: [],
        warnings: [],
      },
      {
        row: 156,
        name: '',
        sku: 'CON-156-ERR',
        description: 'Producto importado sin completar datos',
        price: 0,
        cost: 45.00,
        stock: 12,
        category: '',
        unit: 'PZA',
        supplier: '',
        location: file.location,
        status: 'invalid',
        errors: ['Nombre del producto requerido', 'Precio no puede ser 0', 'Categoría faltante'],
        warnings: [],
      },
      {
        row: 789,
        name: 'Contenedor Industrial 20L',
        sku: 'CON-789-' + file.location?.substring(0,3).toUpperCase(),
        description: 'Contenedor plástico industrial con tapa hermética',
        price: 145.00,
        cost: 98.50,
        stock: Math.floor(Math.random() * 200) + 25,
        category: 'Industrial',
        unit: 'PZA',
        supplier: 'Industrias Plásticas HN',
        location: file.location,
        status: 'valid',
        errors: [],
        warnings: [],
      },
      {
        row: 1245,
        name: 'Escoba Industrial Premium',
        sku: 'ESC-PREM-001',
        description: 'Escoba industrial con mango reforzado',
        price: 125.00,
        cost: 89.75,
        stock: Math.floor(Math.random() * 150) + 40,
        category: 'Limpieza',
        unit: 'PZA',
        supplier: 'Limpieza Total SA',
        location: file.location,
        status: 'duplicate',
        errors: [],
        warnings: ['SKU duplicado encontrado en múltiples ubicaciones'],
      },
    ];

    const analysis: FileAnalysis = {
      totalRows,
      validRows,
      invalidRows,
      duplicates,
      categories: ['Hogar', 'Limpieza', 'Industrial', 'Jardinería', 'Construcción', 'Desechables', 'Organizadores', 'Muebles', 'Baño', 'Cocina'],
      priceRange: {
        min: 2.50, // Producto más barato
        max: 850.00, // Producto más caro
      },
      stockTotal,
      estimatedValue,
    };

    const updatedFile: ExcelFile = {
      ...file,
      status: 'ready',
      records: mockRecords,
      analysis,
    };

    setFiles(prev => prev.map(f => f.name === file.name ? updatedFile : f));
    setIsAnalyzing(false);
  };

  // Simular migración optimizada para grandes volúmenes
  const startMigration = async (file: ExcelFile): Promise<void> => {
    setIsMigrating(true);
    setMigrationProgress(0);

    const totalRecords = file.analysis?.validRows || 0;
    const batchSize = 100; // Procesar en lotes de 100 registros
    const totalBatches = Math.ceil(totalRecords / batchSize);
    
    console.log(`Iniciando migración de ${totalRecords} productos en ${totalBatches} lotes...`);

    for (let batch = 0; batch < totalBatches; batch++) {
      // Simular procesamiento de lote
      const startRecord = batch * batchSize;
      const endRecord = Math.min(startRecord + batchSize, totalRecords);
      const batchCount = endRecord - startRecord;
      
      console.log(`Procesando lote ${batch + 1}/${totalBatches}: registros ${startRecord + 1} a ${endRecord}`);
      
      // Tiempo realista para procesar un lote grande
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Simular generación de códigos de barras en lote
      if (migrationSettings.generateMissingBarcodes) {
        console.log(`Generando ${batchCount} códigos de barras para lote ${batch + 1}`);
      }
      
      // Simular creación de productos e inventario en lote
      console.log(`Creando ${batchCount} productos con inventario en ${file.location}`);
      
      // Actualizar progreso
      const progress = ((batch + 1) / totalBatches) * 100;
      setMigrationProgress(progress);
      
      // Pequeña pausa para mostrar progreso
      if (batch % 5 === 0) {
        await new Promise(resolve => setTimeout(resolve, 200));
      }
    }

    // Simular procesamiento final y índices
    console.log('Generando índices y optimizando base de datos...');
    await new Promise(resolve => setTimeout(resolve, 2000));

    // Marcar archivo como migrado
    setFiles(prev => prev.map(f => f.name === file.name ? { ...f, status: 'migrated' } : f));
    setIsMigrating(false);
    setMigrationDialog(false);
    
    const estimatedValue = file.analysis?.estimatedValue || 0;
    const stockTotal = file.analysis?.stockTotal || 0;
    
    alert(`✅ Migración completada!\n\n📦 ${totalRecords.toLocaleString()} productos importados desde ${file.name}\n💰 Valor total: ${formatCurrency(estimatedValue)}\n📊 Stock total: ${stockTotal.toLocaleString()} unidades\n📍 Ubicación: ${file.location}`);
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const uploadedFiles = Array.from(event.target.files || []);
    
    const newFiles: ExcelFile[] = uploadedFiles.map(file => ({
      name: file.name,
      size: file.size,
      lastModified: new Date(file.lastModified),
      status: 'pending',
    }));

    setFiles(prev => [...prev, ...newFiles]);
    
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const getStatusColor = (status: ExcelFile['status']) => {
    switch (status) {
      case 'pending': return 'default';
      case 'analyzing': return 'info';
      case 'ready': return 'success';
      case 'migrated': return 'primary';
      case 'error': return 'error';
      default: return 'default';
    }
  };

  const getStatusIcon = (status: ExcelFile['status']) => {
    switch (status) {
      case 'pending': return <ExcelIcon />;
      case 'analyzing': return <AnalysisIcon />;
      case 'ready': return <ValidIcon />;
      case 'migrated': return <DatabaseIcon />;
      case 'error': return <ErrorIcon />;
      default: return <ExcelIcon />;
    }
  };

  const migrationSteps = [
    'Seleccionar Archivos',
    'Analizar Contenido',
    'Configurar Migración',
    'Ejecutar Migración'
  ];

  return (
    <Box>
      {/* Encabezado */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" component="h1" gutterBottom fontWeight="bold">
            Migración de Datos Excel
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Importa productos desde archivos Excel existentes
          </Typography>
        </Box>
        
        <Box sx={{ display: 'flex', gap: 2 }}>
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileUpload}
            accept=".xlsx,.xls,.csv"
            multiple
            style={{ display: 'none' }}
          />
          <Button
            variant="outlined"
            startIcon={<UploadIcon />}
            onClick={() => fileInputRef.current?.click()}
          >
            Cargar Archivos
          </Button>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={() => setFiles(existingFiles)}
          >
            Recargar
          </Button>
        </Box>
      </Box>

      {/* Proceso de migración */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom fontWeight="bold">
          Proceso de Migración
        </Typography>
        
        <Stepper activeStep={currentStep} orientation="horizontal">
          {migrationSteps.map((label, index) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
      </Paper>

      {/* Estadísticas */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <ExcelIcon color="primary" sx={{ mr: 1 }} />
                <Typography variant="subtitle2">Archivos Disponibles</Typography>
              </Box>
              <Typography variant="h5" fontWeight="bold">
                {files.length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <ValidIcon color="success" sx={{ mr: 1 }} />
                <Typography variant="subtitle2">Listos para Migrar</Typography>
              </Box>
              <Typography variant="h5" fontWeight="bold">
                {files.filter(f => f.status === 'ready').length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <DatabaseIcon color="primary" sx={{ mr: 1 }} />
                <Typography variant="subtitle2">Migrados</Typography>
              </Box>
              <Typography variant="h5" fontWeight="bold">
                {files.filter(f => f.status === 'migrated').length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <ProductIcon color="secondary" sx={{ mr: 1 }} />
                <Typography variant="subtitle2">Productos Estimados</Typography>
              </Box>
              <Typography variant="h5" fontWeight="bold">
                {files.reduce((sum, f) => sum + (f.analysis?.validRows || 0), 0)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Lista de archivos */}
      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom fontWeight="bold">
          Archivos para Migrar
        </Typography>
        
        {files.length === 0 ? (
          <Alert severity="info">
            No hay archivos disponibles. Carga archivos Excel o verifica la carpeta "Migracion".
          </Alert>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Archivo</TableCell>
                  <TableCell>Ubicación</TableCell>
                  <TableCell>Tamaño</TableCell>
                  <TableCell>Última Modificación</TableCell>
                  <TableCell>Estado</TableCell>
                  <TableCell>Análisis</TableCell>
                  <TableCell>Acciones</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {files.map((file, index) => (
                  <TableRow key={index}>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <ExcelIcon color="primary" />
                        <Typography variant="body2" fontWeight="medium">
                          {file.name}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      {file.location ? (
                        <Chip 
                          label={file.location} 
                          size="small" 
                          icon={<LocationIcon />}
                          variant="outlined" 
                        />
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          Sin asignar
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      {(file.size / 1024).toFixed(1)} KB
                    </TableCell>
                    <TableCell>
                      {file.lastModified.toLocaleDateString('es-HN')}
                    </TableCell>
                    <TableCell>
                      <Chip
                        icon={getStatusIcon(file.status)}
                        label={
                          file.status === 'pending' ? 'Pendiente' :
                          file.status === 'analyzing' ? 'Analizando' :
                          file.status === 'ready' ? 'Listo' :
                          file.status === 'migrated' ? 'Migrado' : 'Error'
                        }
                        color={getStatusColor(file.status)}
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      {file.analysis ? (
                        <Box>
                          <Typography variant="caption" display="block">
                            ✓ {file.analysis.validRows} válidos
                          </Typography>
                          <Typography variant="caption" display="block" color="error">
                            ✗ {file.analysis.invalidRows} inválidos
                          </Typography>
                          <Typography variant="caption" display="block" color="warning.main">
                            ⚠ {file.analysis.duplicates} duplicados
                          </Typography>
                        </Box>
                      ) : (
                        <Typography variant="caption" color="text.secondary">
                          Sin analizar
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        {file.status === 'pending' && (
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<AnalysisIcon />}
                            onClick={() => analyzeExcelFile(file)}
                            disabled={isAnalyzing}
                          >
                            {isAnalyzing ? 'Analizando...' : 'Analizar'}
                          </Button>
                        )}
                        {file.status === 'ready' && (
                          <Button
                            size="small"
                            variant="contained"
                            startIcon={<StartIcon />}
                            onClick={() => {
                              setSelectedFile(file);
                              setMigrationDialog(true);
                            }}
                          >
                            Migrar
                          </Button>
                        )}
                        {file.status === 'migrated' && (
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<DownloadIcon />}
                            onClick={() => alert('📊 Generando reporte de migración...')}
                          >
                            Reporte
                          </Button>
                        )}
                        {file.analysis && (
                          <IconButton
                            size="small"
                            onClick={() => {
                              setSelectedFile(file);
                              console.log('Mostrando detalles del análisis:', file.analysis);
                            }}
                            title="Ver detalles"
                          >
                            <AnalysisIcon />
                          </IconButton>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {/* Dialog de Migración */}
      <Dialog open={migrationDialog} onClose={() => setMigrationDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <DatabaseIcon />
            Configurar Migración - {selectedFile?.name}
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedFile?.analysis && (
            <>
              <Alert severity="info" sx={{ mb: 3 }}>
                <Typography variant="subtitle2">
                  Resumen del archivo: {selectedFile.analysis.validRows} registros válidos de {selectedFile.analysis.totalRows} totales
                </Typography>
                <Typography variant="body2">
                  Valor estimado del inventario: {formatCurrency(selectedFile.analysis.estimatedValue)}
                </Typography>
              </Alert>

              <Typography variant="h6" gutterBottom>
                Configuración de Migración
              </Typography>

              <Grid container spacing={3} sx={{ mb: 3 }}>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                    <input
                      type="checkbox"
                      checked={migrationSettings.overwriteExisting}
                      onChange={(e) => setMigrationSettings({
                        ...migrationSettings,
                        overwriteExisting: e.target.checked
                      })}
                    />
                    <Typography variant="body2">
                      Sobrescribir productos existentes
                    </Typography>
                  </Box>
                  
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                    <input
                      type="checkbox"
                      checked={migrationSettings.generateMissingBarcodes}
                      onChange={(e) => setMigrationSettings({
                        ...migrationSettings,
                        generateMissingBarcodes: e.target.checked
                      })}
                    />
                    <Typography variant="body2">
                      Generar códigos de barras automáticamente
                    </Typography>
                  </Box>
                  
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                    <input
                      type="checkbox"
                      checked={migrationSettings.stockValidation}
                      onChange={(e) => setMigrationSettings({
                        ...migrationSettings,
                        stockValidation: e.target.checked
                      })}
                    />
                    <Typography variant="body2">
                      Validar stock negativo
                    </Typography>
                  </Box>
                </Grid>
                
                <Grid size={{ xs: 12, sm: 6 }}>
                  <FormControl fullWidth sx={{ mb: 2 }}>
                    <InputLabel>Categoría por Defecto</InputLabel>
                    <Select
                      value={migrationSettings.defaultCategory}
                      onChange={(e) => setMigrationSettings({
                        ...migrationSettings,
                        defaultCategory: e.target.value
                      })}
                      label="Categoría por Defecto"
                    >
                      <MenuItem value="Hogar">Hogar</MenuItem>
                      <MenuItem value="Limpieza">Limpieza</MenuItem>
                      <MenuItem value="Construcción">Construcción</MenuItem>
                      <MenuItem value="Plásticos">Plásticos</MenuItem>
                      <MenuItem value="Otros">Otros</MenuItem>
                    </Select>
                  </FormControl>
                  
                  <TextField
                    fullWidth
                    label="Multiplicador de Precios"
                    type="number"
                    value={migrationSettings.priceMultiplier}
                    onChange={(e) => setMigrationSettings({
                      ...migrationSettings,
                      priceMultiplier: parseFloat(e.target.value) || 1.0
                    })}
                    inputProps={{ min: 0.1, max: 10, step: 0.1 }}
                    helperText="1.0 = sin cambios, 1.15 = aumentar 15%"
                  />
                </Grid>
              </Grid>

              {isMigrating && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    Progreso de migración: {migrationProgress.toFixed(0)}%
                  </Typography>
                  <LinearProgress variant="determinate" value={migrationProgress} />
                </Box>
              )}

              <Typography variant="subtitle1" gutterBottom>
                Estadísticas del Archivo
              </Typography>
              
              <Grid container spacing={2}>
                <Grid size={{ xs: 6, sm: 3 }}>
                  <Typography variant="caption" color="text.secondary">
                    Registros Válidos
                  </Typography>
                  <Typography variant="h6" color="success.main">
                    {selectedFile.analysis.validRows}
                  </Typography>
                </Grid>
                <Grid size={{ xs: 6, sm: 3 }}>
                  <Typography variant="caption" color="text.secondary">
                    Registros Inválidos
                  </Typography>
                  <Typography variant="h6" color="error.main">
                    {selectedFile.analysis.invalidRows}
                  </Typography>
                </Grid>
                <Grid size={{ xs: 6, sm: 3 }}>
                  <Typography variant="caption" color="text.secondary">
                    Stock Total
                  </Typography>
                  <Typography variant="h6">
                    {selectedFile.analysis.stockTotal.toLocaleString()}
                  </Typography>
                </Grid>
                <Grid size={{ xs: 6, sm: 3 }}>
                  <Typography variant="caption" color="text.secondary">
                    Categorías
                  </Typography>
                  <Typography variant="h6">
                    {selectedFile.analysis.categories.length}
                  </Typography>
                </Grid>
              </Grid>

              {selectedFile.analysis.categories.length > 0 && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="caption" color="text.secondary">
                    Categorías encontradas:
                  </Typography>
                  <Box sx={{ mt: 1 }}>
                    {selectedFile.analysis.categories.map((category) => (
                      <Chip
                        key={category}
                        label={category}
                        size="small"
                        sx={{ mr: 1, mb: 1 }}
                        variant="outlined"
                      />
                    ))}
                  </Box>
                </Box>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button 
            onClick={() => setMigrationDialog(false)}
            disabled={isMigrating}
          >
            Cancelar
          </Button>
          <Button
            variant="contained"
            startIcon={<StartIcon />}
            onClick={() => selectedFile && startMigration(selectedFile)}
            disabled={isMigrating || !selectedFile}
          >
            {isMigrating ? 'Migrando...' : 'Iniciar Migración'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MigrationPage;